import * as Constants from '../../../constants';

import { faClose, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik } from "formik";
import { useState, useEffect } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import * as Yup from "yup";

export default function UserFormModal({ show, onHide, onSave, user }) {
    const [isLoading, setIsLoading] = useState(true);
    const [userTypes, setUserTypes] = useState([]);
    const [statusTypes, setStatusTypes] = useState([]);

    const validationSchema = Yup.object({
        name: Yup.string()
            .trim()
            .min(3, "Deve ter ao menos 3 caracteres")
            .required("Informe o nome"),
        email: Yup.string()
            .trim()
            .email("Informe um e-mail válido")
            .required("Informe o e-mail"),
        type: Yup.string().notOneOf(["0"], "Selecione o tipo"),
        active: Yup.string().notOneOf(["0"], "Selecione o active")
    });

    const initialValues = {
        name: user?.name || "",
        email: user?.email || "",
        type: user?.type || "0",
        active: user?.active || "0"
    };

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            try {
                setIsLoading(true);

                const token = localStorage.getItem("token");

                const headers = {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                };

                const [userTypesRes, statusTypesRes] = await Promise.all([
                    fetch(Constants.USER_TYPES_LIST, { method: 'GET', signal, headers }),
                    fetch(Constants.USER_STATUS_LIST, { method: 'GET', signal, headers })
                ]);

                if (!userTypesRes.ok) {
                    const err = await userTypesRes.json();
                    throw err;
                }

                if (!statusTypesRes.ok) {
                    const err = await statusTypesRes.json();
                    throw err;
                }

                const userTypesJson = await userTypesRes.json();
                const statusTypesJson = await statusTypesRes.json();

                if (userTypesJson.status === "success") {
                    setUserTypes(userTypesJson.data);
                }

                if (statusTypesJson.status === "success") {
                    setStatusTypes(statusTypesJson.data);
                }

            } catch (error) {

                if (error.name === 'AbortError') return;

                console.error('Erro na requisição:', error.message || error);

            } finally {
                setIsLoading(false);
            }
        };

        fetchData();

        return () => controller.abort();

    }, []);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => {
                const data = user
                    ? { ...values, id: user.id }
                    : values;
                onSave(data);
                resetForm();
                onHide();
            }}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                submitCount
            }) => {
                // Log de erros no console ao tentar submeter
                if (submitCount > 0 && Object.keys(errors).length > 0) {
                    console.warn("Erros de Validação:", errors);
                }

                return (
                    <Modal show={show} onHide={onHide} centered>
                        <Modal.Header closeButton className="bg-light">
                            <Modal.Title>
                                {user ? "Editar Usuário" : "Cadastrar Usuário"}
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <Form noValidate onSubmit={handleSubmit}>

                                {/* Usuário (Nome) */}
                                <Form.Group className="mb-4">
                                    <Form.Label>Usuário</Form.Label>
                                    <Form.Control
                                        name="name"
                                        type="text"
                                        placeholder="Nome do usuário"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.name && !!errors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {/* E-mail (Adicionado aqui) */}
                                <Form.Group className="mb-4">
                                    <Form.Label>E-mail</Form.Label>
                                    <Form.Control
                                        name="email"
                                        type="email"
                                        placeholder="exemplo@email.com"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.email && !!errors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {/* Campo Type */}
                                <Form.Group className="mb-4">
                                    <Form.Label>Tipo</Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={values.type}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.type && !!errors.type}
                                    >
                                        <option value="0">Selecione</option>
                                        {userTypes.map(type => (
                                            <option key={type.id} value={type.id}>
                                                {type.title}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.type}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {/* Campo Status */}
                                <Form.Group className="mb-4">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        name="active"
                                        value={values.active}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.active && !!errors.active}
                                    >
                                        <option value="0">Selecione</option>
                                        {statusTypes.map(type => (
                                            <option key={type.id} value={type.id}>
                                                {type.title}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.active}
                                    </Form.Control.Feedback>
                                </Form.Group>

                            </Form>
                        </Modal.Body>

                        <Modal.Footer>
                            <Button variant="outline-secondary" onClick={onHide}>
                                <FontAwesomeIcon icon={faClose} /> Cancelar
                            </Button>

                            <Button
                                variant="primary"
                                className="px-4"
                                onClick={handleSubmit}
                            >
                                <FontAwesomeIcon icon={faFloppyDisk} /> Salvar Usuário
                            </Button>
                        </Modal.Footer>
                    </Modal>
                );
            }}
        </Formik>
    );
}